import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import type { AdminUser, AiProposal } from '../lib/types';
import { formatDate, formatDateTime, titleCase } from '../lib/format';
import { useSession } from '../lib/session';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Loading,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from '../components/ui';

/**
 * §14 review queue (BL-18).
 *
 * Proposals are grouped by the meeting they came from, because that is how a CSM
 * remembers them: "the Novo weekly", not "proposal 4 of 11". Every item shows
 * the quote it was drawn from, so accepting is a judgement about what was said
 * rather than trust in the model.
 *
 * Accept sends only the fields the reviewer actually changed. The server keeps
 * the model's original next to what was accepted, so an edit is on the record.
 */

type StatusFilter = 'pending_review' | 'accepted' | 'edited_and_accepted' | 'rejected';

const TYPE_LABEL: Record<string, string> = {
  customer_commitment: 'Commitment',
  customer_followup: 'Follow-up',
  task: 'Task',
  opportunity_signal: 'Opportunity',
};

const STATUS_LABEL: Record<string, string> = {
  pending_review: 'Awaiting review',
  accepted: 'Accepted',
  edited_and_accepted: 'Edited, then accepted',
  rejected: 'Rejected',
  expired: 'Expired',
};

export function AiReviewPage() {
  const [status, setStatus] = useState<StatusFilter>('pending_review');
  const [reviewing, setReviewing] = useState<AiProposal | null>(null);

  const query = useQuery({
    queryKey: ['ai-proposals', status],
    queryFn: () => api.get<{ proposals: AiProposal[] }>('/ai-proposals', { status }),
  });

  const groups = useMemo(() => {
    const bySource = new Map<string, { key: string; proposals: AiProposal[]; head: AiProposal }>();
    for (const proposal of query.data?.proposals ?? []) {
      const key = proposal.source?.id ?? `none:${proposal.id}`;
      const group = bySource.get(key) ?? { key, proposals: [], head: proposal };
      group.proposals.push(proposal);
      bySource.set(key, group);
    }
    return [...bySource.values()];
  }, [query.data]);

  return (
    <>
      <PageHeader
        title="AI Review"
        description="Actions proposed from customer calls. Nothing here is recorded until you accept it."
        meta={
          <Select value={status} onChange={(event) => setStatus(event.target.value as StatusFilter)} className="w-56">
            <option value="pending_review">Awaiting review</option>
            <option value="accepted">Accepted</option>
            <option value="edited_and_accepted">Edited, then accepted</option>
            <option value="rejected">Rejected</option>
          </Select>
        }
      />

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && groups.length === 0 && (
        <EmptyState
          title={status === 'pending_review' ? 'Nothing to review' : `No ${STATUS_LABEL[status].toLowerCase()} proposals`}
        />
      )}

      <div className="space-y-4">
        {groups.map(({ key, proposals, head }) => (
          <Card key={key}>
            <CardHeader
              title={head.source?.subject ?? 'Untitled meeting'}
              count={proposals.length}
              description={
                <>
                  {head.account ? <Link to={`/accounts/${head.account.id}`} className="underline">{head.account.name}</Link> : 'No account'}
                  {head.source?.occurredAt && <> · {formatDateTime(head.source.occurredAt)}</>}
                </>
              }
              actions={
                head.source?.url ? (
                  <a href={head.source.url} target="_blank" rel="noreferrer" className="text-xs underline">
                    Open recording
                  </a>
                ) : undefined
              }
            />
            <ul className="divide-y divide-border">
              {proposals.map((proposal) => (
                <li key={proposal.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone="info">{TYPE_LABEL[proposal.proposalType] ?? titleCase(proposal.proposalType)}</Badge>
                      <span className="text-sm font-medium text-ink">{String((proposal.accepted ?? proposal.proposed).title ?? '')}</span>
                      <span className="tnum text-xs text-muted">{Math.round(proposal.confidence * 100)}% sure</span>
                      {proposal.status !== 'pending_review' && (
                        <Badge tone={proposal.status === 'rejected' ? 'neutral' : 'good'}>{STATUS_LABEL[proposal.status]}</Badge>
                      )}
                    </div>
                    <ProposalFacts proposal={proposal} />
                    {proposal.evidence && (
                      <blockquote className="mt-1.5 border-l-2 border-border-strong pl-2 text-xs text-ink-2 italic">
                        {proposal.evidence}
                      </blockquote>
                    )}
                    {proposal.reviewNotes && <p className="mt-1 text-xs text-muted">Note: {proposal.reviewNotes}</p>}
                  </div>
                  {proposal.status === 'pending_review' && (
                    <Button size="sm" variant="primary" onClick={() => setReviewing(proposal)}>
                      Review
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </div>

      {reviewing && <ReviewDialog proposal={reviewing} onClose={() => setReviewing(null)} />}
    </>
  );
}

function ProposalFacts({ proposal }: { proposal: AiProposal }) {
  const p = proposal.accepted ?? proposal.proposed;
  const facts: string[] = [];
  if (p.customerDueAt) facts.push(`Customer date ${formatDate(String(p.customerDueAt))}`);
  if (p.externallyCommitted === true) facts.push('told the customer');
  if (p.waitingFor) facts.push(`Waiting for: ${String(p.waitingFor)}`);
  if (p.customerExpectedAt) facts.push(`expected ${formatDate(String(p.customerExpectedAt))}`);
  if (p.dueAt) facts.push(`Due ${formatDate(String(p.dueAt))}`);
  if (p.signalType) facts.push(titleCase(String(p.signalType)));
  if (p.potentialValueText) facts.push(String(p.potentialValueText));
  if (facts.length === 0) return null;
  return <p className="mt-1 text-xs text-ink-2">{facts.join(' · ')}</p>;
}

/** `<input type="date">` value ↔ the ISO date-time the API speaks. */
const toDateInput = (value: unknown) => (typeof value === 'string' && value ? value.slice(0, 10) : '');
const fromDateInput = (value: string) => (value ? new Date(`${value}T12:00:00`).toISOString() : null);

function ReviewDialog({ proposal, onClose }: { proposal: AiProposal; onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const p = proposal.proposed;

  const [title, setTitle] = useState(String(p.title ?? ''));
  const [description, setDescription] = useState(String(p.description ?? ''));
  const [customerDueAt, setCustomerDueAt] = useState(toDateInput(p.customerDueAt));
  const [externallyCommitted, setExternallyCommitted] = useState(p.externallyCommitted === true);
  const [waitingFor, setWaitingFor] = useState(String(p.waitingFor ?? ''));
  const [customerExpectedAt, setCustomerExpectedAt] = useState(toDateInput(p.customerExpectedAt));
  const [followupAt, setFollowupAt] = useState(toDateInput(p.customerExpectedAt));
  const [dueAt, setDueAt] = useState(toDateInput(p.dueAt));
  const [notes, setNotes] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const users = useQuery({
    queryKey: ['admin-users-lite'],
    queryFn: () => api.get<{ data: AdminUser[] }>('/admin/users'),
    // Non-admins cannot list users; the owner select then offers the reviewer.
    retry: false,
  });
  const ownerOptions = useMemo(() => {
    const list = users.data?.data ?? [];
    return list.length > 0 ? list.map((u) => ({ id: u.id, name: u.fullName })) : [{ id: me.id, name: me.fullName }];
  }, [users.data, me]);
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  const ownerUserId =
    ownerChoice ??
    (proposal.suggestedOwnerUserId && ownerOptions.some((o) => o.id === proposal.suggestedOwnerUserId)
      ? proposal.suggestedOwnerUserId
      : me.id);

  const type = proposal.proposalType;
  const edits = useMemo(() => {
    const candidate: Record<string, unknown> = { title: title.trim() };
    if (type === 'customer_commitment') {
      Object.assign(candidate, {
        description: description.trim() || null,
        customerDueAt: fromDateInput(customerDueAt),
        externallyCommitted,
      });
    }
    if (type === 'customer_followup') {
      Object.assign(candidate, { waitingFor: waitingFor.trim(), customerExpectedAt: fromDateInput(customerExpectedAt) });
    }
    if (type === 'task') {
      Object.assign(candidate, { description: description.trim() || null, dueAt: fromDateInput(dueAt) });
    }
    // Send only what differs, so an untouched proposal is accepted as proposed.
    const changed: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(candidate)) {
      const original = key.endsWith('At') ? fromDateInput(toDateInput(p[key])) : (p[key] ?? null);
      if (JSON.stringify(value ?? null) !== JSON.stringify(original ?? null)) changed[key] = value;
    }
    return changed;
  }, [type, title, description, customerDueAt, externallyCommitted, waitingFor, customerExpectedAt, dueAt, p]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ai-proposals'] });
    void queryClient.invalidateQueries({ queryKey: ['my-work'] });
    void queryClient.invalidateQueries({ queryKey: ['work-items'] });
  };

  const accept = useMutation({
    mutationFn: () =>
      api.post(`/ai-proposals/${proposal.id}/accept`, {
        ownerUserId,
        edits,
        followupAt: type === 'customer_followup' ? (fromDateInput(followupAt) ?? undefined) : undefined,
        overrideWarningReason: overrideReason.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast(`${TYPE_LABEL[type] ?? 'Item'} recorded${Object.keys(edits).length ? ' with your edits' : ''}`);
      invalidate();
      onClose();
    },
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/ai-proposals/${proposal.id}/reject`, { notes: notes.trim() || undefined }),
    onSuccess: () => {
      toast('Proposal rejected');
      invalidate();
      onClose();
    },
  });

  const error = (accept.error ?? reject.error) as unknown;
  const needsReason = error instanceof ApiError && error.requiresOverrideReason;
  const busy = accept.isPending || reject.isPending;
  const canAccept =
    title.trim().length > 0 &&
    (type !== 'customer_commitment' || Boolean(customerDueAt)) &&
    (type !== 'customer_followup' || (Boolean(waitingFor.trim()) && Boolean(followupAt))) &&
    (!needsReason || Boolean(overrideReason.trim()));

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={`Review ${(TYPE_LABEL[type] ?? 'proposal').toLowerCase()}`}
      description={proposal.account ? `${proposal.account.name} · ${proposal.source?.subject ?? ''}` : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={() => reject.mutate()} disabled={busy}>
            Reject
          </Button>
          <Button variant="primary" onClick={() => accept.mutate()} disabled={busy || !canAccept}>
            {Object.keys(edits).length ? 'Accept with edits' : 'Accept'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {proposal.evidence && (
          <Callout tone="info" title={`What was said · ${Math.round(proposal.confidence * 100)}% sure`}>
            <span className="italic">{proposal.evidence}</span>
          </Callout>
        )}

        <Field label="Title" required>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>

        {(type === 'customer_commitment' || type === 'task') && (
          <Field label="Description">
            <Textarea rows={2} value={description} onChange={(event) => setDescription(event.target.value)} />
          </Field>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Owner" required>
            <Select value={ownerUserId} onChange={(event) => setOwnerChoice(event.target.value)}>
              {ownerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Field>

          {type === 'customer_commitment' && (
            <Field label="Date promised to the customer" required hint={p.customerDueAt ? undefined : 'Not stated on the call'}>
              <Input type="date" value={customerDueAt} onChange={(event) => setCustomerDueAt(event.target.value)} />
            </Field>
          )}
          {type === 'task' && (
            <Field label="Due">
              <Input type="date" value={dueAt} onChange={(event) => setDueAt(event.target.value)} />
            </Field>
          )}
          {type === 'customer_followup' && (
            <>
              <Field label="Customer said they would send it by">
                <Input type="date" value={customerExpectedAt} onChange={(event) => setCustomerExpectedAt(event.target.value)} />
              </Field>
              <Field label="Follow up on" required>
                <Input type="date" value={followupAt} onChange={(event) => setFollowupAt(event.target.value)} />
              </Field>
            </>
          )}
        </div>

        {type === 'customer_followup' && (
          <Field label="Waiting for" required>
            <Input value={waitingFor} onChange={(event) => setWaitingFor(event.target.value)} />
          </Field>
        )}
        {type === 'customer_commitment' && (
          <Checkbox
            label="The customer heard this as a promise"
            checked={externallyCommitted}
            onChange={(event) => setExternallyCommitted(event.target.checked)}
          />
        )}

        {needsReason && (
          <Field label="Why commit before Product confirms?" required hint={String((error as ApiError).details?.warning ?? '')}>
            <Textarea rows={2} value={overrideReason} onChange={(event) => setOverrideReason(event.target.value)} />
          </Field>
        )}

        <Field label="Note for the record">
          <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Optional; saved with your decision" />
        </Field>

        {error != null && !needsReason ? <ErrorNotice error={error} /> : null}
        {proposal.expiresAt && <p className="text-xs text-muted">Expires {formatDate(proposal.expiresAt)} if not reviewed.</p>}
      </div>
    </Dialog>
  );
}
